import type { MiniDashboardProps, Section, MetaTotalsResponse } from '../types'

import { FileTextOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Spin, Tooltip } from 'antd'
import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'

import $api from '../../../../API'

export function MiniDashboard({
  scheduledIsActive,
  onScheduledClick,

  builtIsActive,
  onBuiltClick,

  waitingIsActive,
  onWaitingClick,

  voidedIsActive,
  onVoidedClick,

  noPaymentFileIsActive,
  onNoPaymentFileClick,

  pendingIsActive,
  onPendingClick,

  failedIsActive,
  onFailedClick,

  nomiIsActive,
  onNomiClick
}: MiniDashboardProps): JSX.Element | null {
  const [minWidth, setMinWidth] = useState(20 * 16)
  const containerRef = useRef<HTMLDivElement>(null)

  const totalsQuery = useQuery({
    queryKey: ['check-runs', 'meta', 'totals'],

    async queryFn({ signal }) {
      const response = await $api.get<MetaTotalsResponse>('/check-runs/meta/totals', {
        signal
      })

      return response.data
    }
  })

  const isLoading = totalsQuery.isLoading

  useEffect(() => {
    setMinWidth(prev => containerRef.current?.offsetWidth ?? prev)
  }, [isLoading])

  if (isLoading) {
    return (
      <div
        className='flex flex-row gap-4 -mt-2 -mb-1'
        style={{
          minWidth: `${minWidth}px`
        }}
      >
        <div className='flex flex-row gap-8 rounded-lg bg-gray-50 px-8 py-2 w-full select-none relative'>
          <span className='font-lato tracking-wide text-[9.5px] font-bold uppercase text-gray-50'>
            Loading
          </span>

          <div className='flex flex-col justify-start items-center'>
            <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-50'>
              Loading
            </div>

            <div className='font-lato text-lg text-gray-50'>999</div>
          </div>

          <Spin
            spinning
            className='absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-[calc(-50%_+_3px)]'
          />
        </div>
      </div>
    )
  }

  const data: Section[] = [
    {
      label: '',
      stats: [
        {
          label: 'Nomi',
          value: (
            <button
              type='button'
              className={cx('p-2 -m-2', {
                'text-lucent-primary': nomiIsActive
              })}
              onClick={() => {
                onNomiClick()
              }}
            >
              <FileTextOutlined style={{ fontSize: '18px' }} />
            </button>
          ),
          tooltip: 'All check runs with NOMI files'
        }
      ]
    },

    {
      label: 'Today',
      stats: [
        {
          label: 'Scheduled',
          value: (
            <button
              type='button'
              className={cx('p-2 -m-2', {
                'text-lucent-primary': scheduledIsActive
              })}
              onClick={() => {
                onScheduledClick()
              }}
            >
              {totalsQuery.data?.today.scheduled ?? 0}
            </button>
          ),
          tooltip: 'Number of scheduled groups for today'
        },
        {
          label: 'Built',
          value: (
            <button
              type='button'
              className={cx('p-2 -m-2', {
                'text-lucent-primary': builtIsActive
              })}
              onClick={() => {
                onBuiltClick()
              }}
            >
              {totalsQuery.data?.today.built ?? 0}
            </button>
          ),
          tooltip: 'Number of successfully built groups scheduled for today'
        },
        {
          label: 'Waiting',
          value: (
            <button
              type='button'
              className={cx('p-2 -m-2', {
                'text-lucent-primary': waitingIsActive
              })}
              onClick={() => {
                onWaitingClick()
              }}
            >
              {totalsQuery.data?.today.waiting ?? 0}
            </button>
          ),
          tooltip: 'Check runs scheduled for today that are in a pending status'
        },
        {
          label: 'Voided',
          value: (
            <button
              type='button'
              className={cx('border p-2 -m-2', {
                'text-lucent-primary': voidedIsActive
              })}
              onClick={() => {
                onVoidedClick()
              }}
            >
              {totalsQuery.data?.today.voided ?? 0}
            </button>
          ),
          tooltip: 'Check runs scheduled for today that are voided or deleted'
        }
      ]
    },

    {
      label: 'Total',
      stats: [
        {
          label: 'No Payment File',
          value: (
            <button
              type='button'
              className={cx('p-2 -m-2', {
                'text-lucent-primary': noPaymentFileIsActive
              })}
              onClick={() => {
                onNoPaymentFileClick()
              }}
            >
              {totalsQuery.data?.total.no_payment_file ?? 0}
            </button>
          ),
          tooltip: 'All check runs without payment files'
        },
        {
          label: 'Pending',
          value: (
            <button
              type='button'
              className={cx('p-2 -m-2', {
                'text-lucent-primary': pendingIsActive
              })}
              onClick={() => {
                onPendingClick()
              }}
            >
              {totalsQuery.data?.total.pending ?? 0}
            </button>
          ),
          tooltip: 'All check runs in a pending status'
        },
        {
          label: 'Error',
          value: (
            <button
              type='button'
              className={cx('p-2 -m-2', {
                'text-lucent-red': failedIsActive
              })}
              onClick={() => {
                onFailedClick()
              }}
            >
              {totalsQuery.data?.total.failed ?? 0}
            </button>
          ),
          tooltip: 'All check runs in error status'
        }
      ]
    }
  ]

  return (
    <div className='flex flex-row flex-wrap gap-2 sm:gap-4 sm:-mt-2 sm:-mb-1' ref={containerRef}>
      {data.map((section, i) => (
        <div
          key={i}
          className={cx(
            'flex flex-row flex-wrap items-start rounded-lg bg-gray-100 px-3 sm:px-8 py-2',
            {
              'gap-0': i === 0,
              'gap-2 sm:gap-8': i !== 0
            }
          )}
        >
          <span className='font-lato tracking-wide text-[9.5px] font-bold uppercase text-gray-800 flex items-center'>
            {section.label}
          </span>

          {section.stats.map((entry, j) => (
            <Tooltip key={j} title={entry.tooltip ?? ''} showArrow={false}>
              <div className='flex flex-col justify-start items-center'>
                <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-500'>
                  {entry.label}
                </div>

                <div className='font-lato text-lg text-gray-700'>{entry.value}</div>
              </div>
            </Tooltip>
          ))}
        </div>
      ))}
    </div>
  )
}
